/**
 * useCompetitionActor — the `lastModifiedBy` stamp every competition write
 * carries, and the delete that keeps the audit trail honest.
 *
 * Its own file because it is the one piece `useCompetitionAdmin` and
 * `useCompetitionRegistry` both need, and because both of the values it
 * returns are dependency-free by design: putting them in either module would
 * have made the other import from a sibling for no reason.
 */
import { useCallback, useRef } from "react";
import { deleteDoc, updateDoc } from "firebase/firestore";

export function useCompetitionActor(authUser) {
  // Every competition-scoped write records the acting user in `lastModifiedBy`.
  // Two things depend on it. The Firestore rules PIN the field to
  // `request.auth.uid` on the comp doc, groups, sessions and registrations, so a
  // write cannot claim someone else made it; and the audit triggers read it to
  // attribute their entries, because a Firestore trigger has no caller identity
  // of its own. A write that forgets the stamp is refused by the rules rather
  // than logged as anonymous — the same trade the route write path makes.
  //
  // The uid lives in a ref so `withActor` can be dependency-free: it is used in
  // ~30 callbacks whose dependency arrays would otherwise all need `authUser`,
  // and one forgotten entry there would stamp the PREVIOUS signed-in user.
  const actorUidRef = useRef(authUser?.uid || null);
  actorUidRef.current = authUser?.uid || null;

  /**
   * Stamp a write payload with the acting user.
   *
   * `bulkOp` = `{ id, count }` marks the documents of one bulk operation
   * (start-number backfill, Excel import, group/session unassign) so the audit
   * trigger collapses them into a single summary entry instead of one per
   * document — 500 entrants would otherwise mean 500 log entries. The count
   * travels in the marker because only the writer knows it: having the trigger
   * count instead would mean 500 invocations incrementing one document, which
   * is the ~1 write/sec/doc hot-spot.
   */
  const withActor = useCallback((data, bulkOp) => ({
    ...data,
    lastModifiedBy: actorUidRef.current,
    ...(bulkOp ? { _auditBulk: bulkOp } : {}),
  }), []);

  /**
   * Delete a document while keeping the audit trail honest.
   *
   * A Firestore delete carries no payload, so the audit trigger has to
   * attribute it to the `lastModifiedBy` left on the LAST version of the doc —
   * which names whoever edited it before (the admin who ticked "paid", say),
   * not the person who removed it. Stamping the actor immediately before the
   * delete costs one write and makes the entry name the right person. The stamp
   * is not a tracked key, so it produces no entry of its own.
   */
  const deleteAttributed = useCallback(async (ref) => {
    // A doc that is already gone needs no attribution — and Firestore's delete
    // is a no-op on a missing doc, so swallowing this keeps the flow working.
    await updateDoc(ref, { lastModifiedBy: actorUidRef.current }).catch(() => {});
    await deleteDoc(ref);
  }, []);

  return { withActor, deleteAttributed };
}
